import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Receipt, Plus, Trash2, Eye, Search, ChevronLeft, ChevronRight, X, Calendar, AlertCircle, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { useTenantFormat } from '../hooks/useTenantFormat';
import { api } from '../lib/api';
import Drawer from '../components/Drawer';

const INPUT = 'w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-900 text-sm placeholder:text-gray-400 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition-colors';
const PAGE_SIZE = 25;

const PERIOD_OPTIONS = [
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'last_3', label: 'Last 3 Months' },
  { key: 'custom', label: 'Custom Range' },
];

function getDateRange(key) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  switch (key) {
    case 'this_month': return { from_date: `${y}-${String(m+1).padStart(2,'0')}-01`, to_date: null };
    case 'last_month': { const d = new Date(y, m-1, 1); return { from_date: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`, to_date: `${y}-${String(m+1).padStart(2,'0')}-01` }; }
    case 'last_3': { const d = new Date(y, m-2, 1); return { from_date: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`, to_date: null }; }
    default: return { from_date: null, to_date: null };
  }
}

const CAT_COLORS = {
  Rent: 'bg-blue-50 text-blue-700 border-blue-200',
  Electricity: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  Stationery: 'bg-pink-50 text-pink-700 border-pink-200',
  Maintenance: 'bg-orange-50 text-orange-700 border-orange-200',
  Transport: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  Marketing: 'bg-purple-50 text-purple-700 border-purple-200',
  Salary: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  Salaries: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  Other: 'bg-gray-50 text-gray-600 border-gray-200',
};
function catChipCls(cat) { return CAT_COLORS[cat] || 'bg-gray-50 text-gray-600 border-gray-200'; }

export default function Expenses() {
  const { token } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const { formatMoney, taxLabel } = useTenantFormat();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [period, setPeriod] = useState('this_month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);

  const [addOpen, setAddOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const dateRange = useMemo(() => {
    if (period === 'custom') return { from_date: customFrom || null, to_date: customTo || null };
    return getDateRange(period);
  }, [period, customFrom, customTo]);

  // Load categories once
  useEffect(() => {
    if (!token) return;
    api.expenseCategories(token).then(r => {
      const canonical = r.canonical || [];
      const all = r.items || [];
      const custom = all.filter(c => !canonical.includes(c));
      setCategories([...canonical, ...custom]);
    }).catch(() => {});
  }, [token]);

  // Load summary when date range changes
  const loadSummary = useCallback(async () => {
    if (!token) return;
    try {
      const params = {};
      if (dateRange.from_date) params.from_date = dateRange.from_date;
      if (dateRange.to_date) params.to_date = dateRange.to_date;
      const r = await api.expenseSummary(token, params);
      setSummary(r);
    } catch { setSummary(null); }
  }, [token, dateRange]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // Load list
  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE };
      if (search) params.q = search;
      if (catFilter) params.category = catFilter;
      if (dateRange.from_date) params.from_date = dateRange.from_date;
      if (dateRange.to_date) params.to_date = dateRange.to_date;
      const r = await api.expensesList(token, params);
      setItems(r.items || []);
      setTotal(r.total ?? 0);
    } catch (e) {
      setItems([]); setTotal(0);
      toast.show(e?.message || 'Failed to load expenses', 'error');
    } finally { setLoading(false); }
  }, [token, page, search, catFilter, dateRange, toast]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!deleteItem) return;
    setDeleting(true);
    try {
      await api.expenseDelete(token, deleteItem.id);
      toast.show('Expense deleted', 'success');
      setDeleteItem(null);
      load(); loadSummary();
    } catch (e) {
      const msg = e?.code === 'auto_expense_locked'
        ? 'Linked to Salary Voucher — delete the voucher to remove this expense.'
        : (e?.message || 'Delete failed');
      toast.show(msg, 'error');
      setDeleteItem(null);
    } finally { setDeleting(false); }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const byCategory = summary?.by_category || [];

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight" data-testid="expenses-title">Expenses</h1>
          <p className="text-gray-500 text-sm mt-1">{total} expense{total !== 1 ? 's' : ''} in period</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Period picker */}
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-1 py-1">
            {PERIOD_OPTIONS.map(p => (
              <button key={p.key} data-testid={`period-${p.key}`}
                onClick={() => { setPeriod(p.key); setPage(1); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${period === p.key ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
                {p.label}
              </button>
            ))}
          </div>
          {period === 'custom' && (
            <div className="flex items-center gap-2">
              <input type="date" data-testid="custom-from" value={customFrom} onChange={e => { setCustomFrom(e.target.value); setPage(1); }}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-700" />
              <span className="text-gray-400 text-xs">to</span>
              <input type="date" data-testid="custom-to" value={customTo} onChange={e => { setCustomTo(e.target.value); setPage(1); }}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-700" />
            </div>
          )}
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        {/* Total */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5" data-testid="kpi-total">
          <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Total This Period</p>
          <p className="text-2xl font-extrabold text-gray-900" data-testid="kpi-total-value">{formatMoney(summary?.total ?? 0)}</p>
        </div>
        {/* Category Breakdown chips */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5 overflow-hidden" data-testid="kpi-categories">
          <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-2.5">By Category</p>
          <div className="flex gap-2 overflow-x-auto pb-1 -mb-1 scrollbar-thin">
            {byCategory.length === 0 && <span className="text-gray-400 text-xs">No data</span>}
            {byCategory.map(bc => (
              <button key={bc.category} data-testid={`cat-chip-${bc.category}`}
                onClick={() => { setCatFilter(prev => prev === bc.category ? '' : bc.category); setPage(1); }}
                className={`flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border transition-all cursor-pointer ${catFilter === bc.category ? 'ring-2 ring-indigo-400 ring-offset-1' : ''} ${catChipCls(bc.category)}`}>
                {bc.category} · {formatMoney(bc.total)} · {bc.count}
              </button>
            ))}
          </div>
        </div>
        {/* Entries */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5" data-testid="kpi-entries">
          <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Entries</p>
          <p className="text-2xl font-extrabold text-gray-900" data-testid="kpi-entries-value">{summary?.count ?? total}</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <select data-testid="cat-filter" value={catFilter} onChange={e => { setCatFilter(e.target.value); setPage(1); }}
          className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-700 bg-white outline-none focus:border-indigo-400 cursor-pointer">
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input data-testid="expense-search" placeholder="Search vendor, note..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <div className="flex-1" />
        <button data-testid="add-expense-btn" onClick={() => setAddOpen(true)}
          className="flex items-center gap-2 px-5 h-11 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 text-white text-sm font-bold hover:shadow-lg hover:shadow-teal-500/25 transition-all">
          <Plus size={16} /> Log Expense
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-visible relative">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="expenses-table">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/80">
                <th className="text-left px-4 py-3 text-xs text-gray-500 font-bold uppercase tracking-wider">Date</th>
                <th className="text-left px-4 py-3 text-xs text-gray-500 font-bold uppercase tracking-wider">Category</th>
                <th className="text-right px-4 py-3 text-xs text-gray-500 font-bold uppercase tracking-wider">Amount</th>
                <th className="text-left px-4 py-3 text-xs text-gray-500 font-bold uppercase tracking-wider">Vendor / Narration</th>
                <th className="text-left px-4 py-3 text-xs text-gray-500 font-bold uppercase tracking-wider">Reference</th>
                <th className="text-left px-4 py-3 text-xs text-gray-500 font-bold uppercase tracking-wider">Source</th>
                <th className="text-right px-4 py-3 text-xs text-gray-500 font-bold uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                [1,2,3,4,5].map(i => (
                  <tr key={i}><td colSpan={7} className="px-4 py-4"><div className="h-5 bg-gray-100 rounded animate-pulse" /></td></tr>
                ))
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center">
                    <Receipt size={40} className="text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm" data-testid="expenses-empty">No expenses in this period. Click + Log Expense to add one.</p>
                  </td>
                </tr>
              ) : items.map(e => {
                const isSalary = e.source === 'salary_voucher';
                return (
                  <tr key={e.id} data-testid={`expense-row-${e.id}`}
                    className={`border-b border-gray-50 hover:bg-gray-50/60 transition-colors ${isSalary ? 'cursor-pointer' : ''}`}
                    onClick={() => { if (isSalary && e.voucher_entry_id) navigate(`/vouchers/${e.voucher_entry_id}`); }}>
                    <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
                      {e.expense_date ? new Date(e.expense_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold border ${catChipCls(e.category)}`}>{e.category || '—'}</span>
                        {isSalary && <span className="px-1.5 py-0.5 rounded text-[10px] font-extrabold bg-purple-100 text-purple-700 border border-purple-200" data-testid={`auto-badge-${e.id}`}>AUTO</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900 tabular-nums">{formatMoney(e.amount)}</td>
                    <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate">{e.vendor_name || e.note || '—'}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs font-mono">{e.reference || '—'}</td>
                    <td className="px-4 py-3">
                      {isSalary ? (
                        <span className="text-purple-600 text-xs font-semibold">Salary Voucher</span>
                      ) : (
                        <span className="text-gray-400 text-xs">Manual</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {isSalary ? (
                          <button data-testid={`view-expense-${e.id}`}
                            onClick={ev => { ev.stopPropagation(); if (e.voucher_entry_id) navigate(`/vouchers/${e.voucher_entry_id}`); }}
                            className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors" title="View voucher">
                            <Eye size={16} />
                          </button>
                        ) : (
                          <button data-testid={`view-expense-${e.id}`}
                            onClick={ev => ev.stopPropagation()}
                            className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors" title="View">
                            <Eye size={16} />
                          </button>
                        )}
                        <div className="relative group">
                          <button data-testid={`delete-expense-${e.id}`}
                            onClick={ev => { ev.stopPropagation(); if (!isSalary) setDeleteItem(e); }}
                            disabled={isSalary}
                            className={`p-2 rounded-lg transition-colors ${isSalary ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-red-50 text-gray-400 hover:text-red-500'}`}
                            title={isSalary ? 'Linked to Salary Voucher — delete the voucher to remove this expense.' : 'Delete'}>
                            <Trash2 size={16} />
                          </button>
                          {isSalary && (
                            <div className="absolute bottom-full right-0 mb-2 w-60 p-2.5 rounded-lg bg-gray-900 text-white text-xs shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50" data-testid={`salary-tooltip-${e.id}`}>
                              <AlertCircle size={12} className="inline mr-1 text-amber-400" />
                              Linked to Salary Voucher — delete the voucher to remove this expense.
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">Page {page} of {totalPages} · {total} total</p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} data-testid="expenses-prev"
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                <ChevronLeft size={14} /> Prev
              </button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} data-testid="expenses-next"
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirm Modal */}
      {deleteItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6" onClick={ev => ev.stopPropagation()}>
            <h3 className="text-lg font-extrabold text-gray-900 mb-2">Delete Expense?</h3>
            <p className="text-gray-500 text-sm mb-5">Delete <span className="font-semibold text-gray-700">{deleteItem.category}</span> expense of <span className="font-semibold text-gray-700">{formatMoney(deleteItem.amount)}</span>?</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteItem(null)} className="flex-1 h-11 rounded-xl border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors">Cancel</button>
              <button data-testid="confirm-delete" onClick={handleDelete} disabled={deleting}
                className="flex-1 h-11 rounded-xl bg-red-500 text-white font-bold text-sm hover:bg-red-600 transition-colors disabled:opacity-50">
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Log Expense Drawer */}
      <ExpenseDrawer open={addOpen} onClose={() => setAddOpen(false)} token={token} categories={categories}
        onSuccess={() => { setAddOpen(false); load(); loadSummary(); }} />
    </div>
  );
}

function ExpenseDrawer({ open, onClose, token, categories, onSuccess }) {
  const toast = useToast();
  const { taxLabel: expTaxLabel } = useTenantFormat();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ category: categories[0] || 'Miscellaneous', amount: '', vendor_name: '', expense_date: new Date().toISOString().split('T')[0], gst_amount: '', note: '', reference: '' });
  }, [open, categories]);

  const submit = async () => {
    if (!form.category) { toast.show('Category required', 'error'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.show('Amount required', 'error'); return; }
    setSaving(true);
    try {
      const body = { category: form.category, amount: parseFloat(form.amount), vendor_name: form.vendor_name || undefined, expense_date: form.expense_date || undefined, gst_amount: form.gst_amount ? parseFloat(form.gst_amount) : undefined, note: form.note || undefined, reference: form.reference || undefined };
      await api.expensesCreate(token, body);
      toast.show('Expense logged', 'success');
      onSuccess();
    } catch (e) { toast.show(e?.message || 'Save failed', 'error'); } finally { setSaving(false); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <Drawer open={open} onClose={onClose} title="Log Expense">
      <div className="space-y-4">
        <label className="block" htmlFor="exp-cat">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Category *</span>
          <select id="exp-cat" data-testid="expense-field-category" className={`mt-1.5 ${INPUT} appearance-none cursor-pointer`} value={form.category || ''} onChange={e => set('category', e.target.value)}>
            {categories.map(c => <option key={c} value={c} className="bg-white">{c}</option>)}
          </select>
        </label>
        {[['amount', 'Amount *', 'number'], ['expense_date', 'Date', 'date'], ['vendor_name', 'Vendor Name', 'text'], ['reference', 'Reference', 'text'], ['gst_amount', `${expTaxLabel} Amount`, 'number']].map(([k, l, t]) => (
          <label key={k} className="block" htmlFor={`exp-${k}`}>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{l}</span>
            <input id={`exp-${k}`} data-testid={`expense-field-${k}`} type={t} className={`mt-1.5 ${INPUT}`} value={form[k] || ''} onChange={e => set(k, e.target.value)} placeholder={l.replace(' *', '')} />
          </label>
        ))}
        <label className="block" htmlFor="exp-note">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Notes</span>
          <textarea id="exp-note" data-testid="expense-field-note" className={`mt-1.5 ${INPUT} h-20 resize-none`} value={form.note || ''} onChange={e => set('note', e.target.value)} placeholder="Optional notes..." />
        </label>
        <button data-testid="expense-save-btn" onClick={submit} disabled={saving}
          className="w-full h-12 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-bold text-sm hover:shadow-lg hover:shadow-teal-500/25 transition-all disabled:opacity-50 mt-2">
          {saving ? <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin mx-auto" /> : 'Log Expense'}
        </button>
      </div>
    </Drawer>
  );
}
